import React, { useMemo } from 'react';
import { FaMapMarkerAlt, FaClock, FaPhone, FaGlobe } from 'react-icons/fa';
import { clampRating0to5 } from '../../utils/ratingStars';

/** API: day_of_week 0 = lundi … 6 = dimanche */
const DAY_LABELS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

function formatTimeSlot(t) {
  if (t == null || t === '') return '';
  const s = String(t);
  return s.length >= 5 ? s.slice(0, 5) : s;
}

/** JS getDay(): 0 = dimanche → index API 6…0 */
function getApiDayOfWeek() {
  const js = new Date().getDay();
  return js === 0 ? 6 : js - 1;
}

const AboutSection = ({ 
  restaurant, 
  reviews = [], 
  showReviews, 
  setShowReviews, 
  renderStars,
  formatDate 
}) => {
  const sortedHours = useMemo(() => {
    const list = restaurant?.hours;
    if (!Array.isArray(list) || list.length === 0) return [];
    return [...list].sort((a, b) => (a.day_of_week ?? 0) - (b.day_of_week ?? 0));
  }, [restaurant?.hours]);

  const todayApi = getApiDayOfWeek();

  return (
    <div className="bg-white rounded-lg p-6 mb-8">
      <h2 className="text-2xl font-bold mb-6" style={{ color: 'rgb(5, 70, 37)' }}>
        À propos
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Informations du restaurant */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Informations</h3>
          <div className="space-y-3">
            <div className="flex items-center">
              <FaMapMarkerAlt className="w-4 h-4 text-gray-500 mr-3" />
              <span className="text-gray-700">{restaurant?.address || 'Adresse non disponible'}</span>
            </div>
            <div className="flex items-center">
              <FaPhone className="w-4 h-4 text-gray-500 mr-3" />
              <span className="text-gray-700">{restaurant?.phone || 'Téléphone non disponible'}</span>
            </div>
            {restaurant?.website && (
              <div className="flex items-center">
                <FaGlobe className="w-4 h-4 text-gray-500 mr-3" />
                <a 
                  href={restaurant.website} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="transition-colors"
                  style={{ color: 'rgb(5, 70, 37)', '&:hover': { color: 'rgb(0, 53, 21)' } }}
                >
                  {restaurant.website}
                </a>
              </div>
            )}
            <div className="flex items-center">
              <FaClock className="w-4 h-4 text-gray-500 mr-3" />
              <span className="text-gray-700">
                {restaurant?.is_open ? 'Ouvert maintenant' : 'Fermé'}
              </span>
            </div>
          </div>
        </div>

        {/* Avis récents */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Avis récents</h3>
            <button
              onClick={() => setShowReviews && setShowReviews(true)}
              className="text-sm font-medium transition-colors"
              style={{ color: 'rgb(5, 70, 37)', '&:hover': { color: 'rgb(0, 53, 21)' } }}
            >
              Voir tous les avis
            </button>
          </div>
          
          {reviews.length > 0 ? (
            <div className="space-y-3">
              {reviews.slice(0, 3).map((review, index) => (
                <div key={index} className="border-l-4 pl-4" style={{ borderLeftColor: 'rgb(5, 70, 37)' }}>
                  <div className="flex items-center mb-1">
                    {renderStars && renderStars(clampRating0to5(review.rating ?? 0))}
                    <span className="ml-2 text-sm text-gray-600">
                      {review.user_name || 'Utilisateur anonyme'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 line-clamp-2">
                    {review.comment || 'Aucun commentaire'}
                  </p>
                  <span className="text-xs text-gray-500">
                    {formatDate ? formatDate(review.created_at) : new Date(review.created_at).toLocaleDateString('fr-FR')}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">Aucun avis pour le moment</p>
          )}
        </div>
      </div>

      {(sortedHours.length > 0 || restaurant?.opening_hours) && (
        <div className="mt-8 pt-8 border-t border-gray-200">
          <h3 className="text-lg font-semibold mb-4" style={{ color: 'rgb(5, 70, 37)' }}>
            Horaires d&apos;ouverture
          </h3>
          {sortedHours.length > 0 ? (
            <ul className="space-y-2 max-w-md">
              {sortedHours.map((h, idx) => {
                const dow = h.day_of_week ?? 0;
                const isToday = dow === todayApi;
                const label = DAY_LABELS[dow] ?? `Jour ${dow}`;
                let line = 'Fermé';
                if (!h.is_closed && h.open_time && h.close_time) {
                  line = `${formatTimeSlot(h.open_time)} – ${formatTimeSlot(h.close_time)}`;
                } else if (!h.is_closed && (h.open_time || h.close_time)) {
                  line = [formatTimeSlot(h.open_time), formatTimeSlot(h.close_time)].filter(Boolean).join(' – ') || '—';
                } else if (h.is_closed) {
                  line = 'Fermé';
                }
                return (
                  <li
                    key={`${dow}-${idx}`}
                    className={`flex justify-between text-sm gap-4 ${isToday ? 'font-semibold rounded px-2 py-1 -mx-2' : 'text-gray-700'}`}
                    style={isToday ? { backgroundColor: 'rgba(5, 70, 37, 0.06)' } : undefined}
                  >
                    <span className={isToday ? 'text-gray-900' : ''}>
                      {label}
                      {isToday ? " (aujourd'hui)" : ''}
                    </span>
                    <span className="text-gray-800 tabular-nums shrink-0">{line}</span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-gray-700 whitespace-pre-line text-sm">{restaurant.opening_hours}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default AboutSection;